import React from "react";
import {
  Box,
  Chip,
  Divider,
  Link,
  Typography,
} from "@mui/material";
import { OpenInNew, WarningAmberOutlined } from "@mui/icons-material";
import HPButton from "@/components/common/Button";
import type { ModalProps } from "@/_constants/modal";
import type { GuardrailConflict, GuardrailConflictType } from "@models/Dataset";
import { getDashboardTabUrl } from "@/utils/slug-utils";
import {
  CONFLICT_LABELS,
  CONFLICT_CHIP_LABELS,
  CONFLICT_COLOR,
  WIDGET_TYPE_LABELS,
} from "@/utils/guardrail-conflict-utils";

// ─── Conflict card ────────────────────────────────────────────────────────────

const ConflictCard: React.FC<{ conflict: GuardrailConflict }> = ({ conflict }) => {
  const tabUrl = getDashboardTabUrl(conflict.dashboardId, conflict.dashboardName, conflict.tabName);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "warning.light",
        borderRadius: "8px",
        p: 1.5,
        bgcolor: "#FFFBF0",
      }}
    >
      {/* Widget type badge + title */}
      <Box className="flex items-center gap-2 mb-0.5">
        <Chip
          size="small"
          label={WIDGET_TYPE_LABELS[conflict.widgetType] ?? conflict.widgetType}
          sx={{ fontSize: "0.7rem", height: 20, bgcolor: "#F0F0F0" }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {conflict.widgetTitle}
        </Typography>
      </Box>

      {/* Dashboard / Tab — clickable link */}
      <Link
        href={tabUrl}
        target="_blank"
        rel="noopener noreferrer"
        underline="always"
        variant="caption"
        color="primary"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        {conflict.dashboardName} / {conflict.tabName}
        <OpenInNew sx={{ fontSize: 11 }} />
      </Link>

      {/* Conflict reason */}
      <Typography variant="caption" sx={{ color: "warning.dark" }} display="block" className="mt-0.5">
        {CONFLICT_LABELS[conflict.conflictType]}
        {conflict.widgetTimeUnit && ` (${conflict.widgetTimeUnit})`}
      </Typography>
    </Box>
  );
};

// ─── Modal ────────────────────────────────────────────────────────────────────

interface GuardrailCompatibilityModalProps extends ModalProps {
  conflicts: GuardrailConflict[];
  onSaveAnyway: () => void;
}

const GuardrailCompatibilityModal: React.FC<GuardrailCompatibilityModalProps> = ({
  conflicts,
  onSaveAnyway,
  cancelAction,
  closeAction,
}) => {
  const handleCancel = cancelAction ?? closeAction;

  const grouped = conflicts.reduce<Partial<Record<GuardrailConflictType, number>>>(
    (acc, c) => ({ ...acc, [c.conflictType]: (acc[c.conflictType] ?? 0) + 1 }),
    {},
  );

  return (
    <div className="bg-white w-full rounded-lg shadow-lg flex flex-col mx-auto min-w-[560px] max-w-[640px]">
      {/* Header */}
      <div className="p-6 flex items-center gap-3">
        <WarningAmberOutlined sx={{ color: "warning.main", fontSize: 28 }} />
        <div>
          <h2 className="text-lg font-semibold">Compatibility Warning</h2>
          <p className="text-sm text-gray-500">
            {conflicts.length} visualization{conflicts.length !== 1 ? "s" : ""} will become out of policy
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="border-t border-b border-hp-border-default">
        <div className="p-6 flex flex-col gap-4" style={{ maxHeight: 400, overflowY: "auto" }}>
          <Typography variant="body2" color="text.secondary">
            The following visualizations no longer match the proposed rules. They will continue to render
            but must be updated before they can be re-saved.
          </Typography>

          {/* Conflict type summary chips */}
          <Box className="flex flex-wrap gap-2">
            {(Object.entries(grouped) as [GuardrailConflictType, number][]).map(([type, count]) => (
              <Chip
                key={type}
                size="small"
                label={`${count}× ${CONFLICT_CHIP_LABELS[type]}`}
                color={CONFLICT_COLOR[type]}
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
              />
            ))}
          </Box>

          <Divider />

          {/* Per-visualization cards */}
          <Box className="flex flex-col gap-2">
            {conflicts.map((c, i) => (
              <ConflictCard key={i} conflict={c} />
            ))}
          </Box>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 p-4">
        <HPButton
          ariaLabel="Cancel"
          variant="text"
          size="medium"
          color="primary"
          onClick={handleCancel}
        >
          Cancel
        </HPButton>
        <HPButton
          ariaLabel="Save anyway"
          variant="contained"
          size="medium"
          onClick={onSaveAnyway}
        >
          Save Anyway
        </HPButton>
      </div>
    </div>
  );
};

export default GuardrailCompatibilityModal;
